/**
 * COPYRIGHT NOTICE
 * This file is categorized as "Custom Source Code"
 * and is subject to the terms and conditions defined in the
 * "LICENSE.txt" file, which is part of this source code package.
 */
import { useState, useEffect } from 'react';
import {
	Keyboard,
} from 'react-native';
import {
	Box,
	Button,
	Column,
	Icon,
	Input,
	Row,
	Text,
	useToast,
} from 'native-base';
import {
	FontAwesome5,
	MaterialIcons,
} from '@expo/vector-icons';
import {
	USER,
	EMAIL,
} from '../constants/Vars';
import {
	setProgressMessage,
	setProgressPercentage,
} from '../functions/setProgress';
import ScreenContainer from '../OneHat/components/ScreenContainer';
import { useDimensions } from '@react-native-community/hooks';
import {
	deleteCachedFiles,
} from '../functions/CacheSystem';
import { doLogout } from '../models/Thunks/setQuestion'; // I was getting circular dependency errors, so I combined lots of functions in this file
import setPref from '../OneHat/functions/setPref';
import getIsMobile from '../OneHat/functions/getIsMobile';
import getTokenHeaders from '../OneHat/functions/getTokenHeaders';
import testProps from '../OneHat/functions/testProps';
import withRedux from '../OneHat/data/hoc/withRedux';
import oneHatData from '@onehat/data';
import Loading from '../OneHat/components/Loading';
import ErrorMsg from '../OneHat/components/ErrorMsg';
import _ from 'lodash';


function Account(props) {
	const {
			user,
			email,
			currentQuestion,
			actions,
		} = props,
		screen = useDimensions().screen,
		toast = useToast(),
		isMobile = getIsMobile(),
		[isSpinnerShown, setIsSpinnerShown] = useState(false),
		[spinnerMessage, setSpinnerMessage] = useState(null),
		[error, setError] = useState(false),
		[emailValue, setEmailValue] = useState(email),
		[password, setPassword] = useState(),
		Secure = oneHatData.getRepository('Secure'),
		Users = oneHatData.getRepository('Users'),
		Questions = oneHatData.getRepository('Questions'),
		Songs = oneHatData.getRepository('Songs'),
		songCount = Songs.entities.length,
		Events = oneHatData.getRepository('Events'),
		maxWidth = Math.min(parseInt(screen.width * 0.72), 400),
		login = async () => {
			try {
				onBeginProcess('Logging in...');
				setProgressMessage('Login');
				setProgressPercentage(0);

				Keyboard.dismiss();

				await deleteCachedFiles();
				setProgressPercentage(20);

				// Login user
				const user = await Users.login({
					email: emailValue,
					password,
				});
				setProgressPercentage(40);
				
				// Login successful (otherwise would have thrown an Error)
				actions.setUser(user);
				actions.setEmail(user.email);

				// Apply auth token to all Repositories
				const headers = getTokenHeaders();
				oneHatData.setOptionsOnAllRepositories({ headers }); // already created
				oneHatData.setRepositoryGlobals({ headers }); // not yet created

				// Save vars for later reuse
				if (isMobile) {
					await Secure.add({ key: USER, value: JSON.stringify(user), });
					setPref(EMAIL, user.email);
				}
				setProgressPercentage(60);

				setPassword(); // Clear the password field

				// Reload the Questions and Songs repositories
				await Questions.clearAll();
				await Questions.sync();
				Questions.decodeHtmlEntities();
				setProgressPercentage(70);

				await Songs.clearAll();
				await Songs.sync();

				await Events.clearAll();
				await Events.sync();

				setProgressPercentage(100);

				if (currentQuestion) {
					// Now that new questions and songs have been loaded set the currentQuestion
					actions.setQuestionById(currentQuestion.id);
				}

			} catch(error) {
				const msg = error && error.message;
				onError(msg);
			}
			onEndProcess();
		},
		logout = async () => {
			try {
				onBeginProcess('Logging out...');
				setPassword();

				await doLogout();

				const songCount = Songs.getNonGated().length;
				setSongCount(songCount);

			} catch(error) {
				onError(error.message);
			}
			onEndProcess();
		},
		forgotPassword = async () => {

			// TODO: Contact server, verify email address and send password recovery email
			// or return error message

			const result = await Users.forgotPassword({
				email: emailValue,
			});

			if (result) {
				toast.show({
					title: 'A password recovery email was sent to that address.',
					duration: 800,
					isClosable: false,
					variant: 'subtle',
				});
			} else {
				toast.show({
					title: result.message,
					duration: 800,
					isClosable: false,
					variant: 'subtle',
				});
			}

		},
		onBeginProcess = (message) => {
			setError();
			setIsSpinnerShown(true);
			setSpinnerMessage(message);
		},
		onEndProcess = () => {
			setError();
			setIsSpinnerShown(false);
		},
		onError = (message) => {
			setError(message);
		};

	const
		headingStyle = {
			fontSize: 20,
			fontWeight: 'bold',
			mb: 2,
		},
		buttonProps = {
			bg: 'trueGray.200',
			mb: 2,
			borderBottomColor: 'trueGray.400',
			borderBottomWidth: 1,
		},
		inputProps = {
			borderColor: 'trueGray.400',
			borderWidth: 1,
			variant: 'filled',
			autoCapitalize: 'none',
			size: 'xl',
			mb: 2,
			w: '100%',
		};

	return <ScreenContainer screenName="Login">
		
				<Column flex={1} w="100%" p={10}
					borderTopColor="trueGray.200"
					borderTopWidth={3}
					borderBottomColor="trueGray.300"
					borderBottomWidth={3}
				>
					<Text {...headingStyle}>WSCinME Account</Text>

					{!isMobile && <Text>This web app does not allow login. Please use one of the free mobile apps to access your account data.</Text>}
					{isMobile && <Box>
						{isSpinnerShown && <Row justifyContent="flex-start" alignItems="center">
												<Loading minHeight={30} w={30} />
												<Text>{spinnerMessage}</Text>
											</Row>}
						{!isSpinnerShown && <Box>
												{user && <Text fontSize={15} fontStyle="italic">{user.email}</Text>}
												<Text fontSize={15} mb={2} fontStyle="italic">{'Current access: ' + songCount + ' songs'}</Text>
												{user && <Button
															{...testProps('logoutBtn')}
															{...buttonProps}
															leftIcon={<Icon as={FontAwesome5} name="sign-out-alt" />}
															onPress={logout}
															mt={3}
															bg="primary.800"
															isDisabled={!user && (!emailValue || !password)}
														>Logout</Button>}
								
								
												{!user && <Input
															{...testProps('email')}
															{...inputProps}
															placeholder="Email"
															value={emailValue}
															onChangeText={setEmailValue}
															onSubmitEditing={login}
														/>}
												{!user && <Input
															{...testProps('password')}
															{...inputProps}
															placeholder="Password"
															value={password}
															onChangeText={setPassword}
															secureTextEntry={true}
															type="password"
															overflow="visible"
															InputRightElement={<Icon
																as={<MaterialIcons name="visibility-off" />}
																size={5}
																mr="2"
																color="muted.400"
															/>}
															onSubmitEditing={login}
														/>}
								
												{error && <ErrorMsg style={{ maxWidth }}>{error}</ErrorMsg>}
												{!user && <Button
															{...testProps('loginBtn')}
															{...buttonProps}
															leftIcon={<Icon as={FontAwesome5} name="sign-in-alt" />}
															onPress={login}
															mt={3}
															bg="primary.800"
															isDisabled={!user && (!emailValue || !password)}
														>Login</Button>}
								
												{!user && <Button
															{...testProps('forgotPasswordBtn')}
															{...buttonProps}
															leftIcon={<Icon as={FontAwesome5} name="lightbulb" size="sm" />}
															onPress={forgotPassword}
															mt={3}
															bg="trueGray.300"
															w="70%"
															alignSelf="center"
															isDisabled={!emailValue}
														>Forgot Password</Button>}
											</Box>}
								</Box>}

					
				</Column>
			
			</ScreenContainer>;
}

export default withRedux(Account, [
	'user',
	'email',
	'currentQuestion',
]);